import type { Observation } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  OBSERVATION_MODE_LATEST,
  observationReferenceStrings,
} from '@seen/core/observationReferences'
import type { EditorState } from '@tiptap/pm/state'
import { type Editor, useEditorState } from '@tiptap/react'
import { isEqual } from 'lodash-es'
import { useMemo } from 'react'
import { toast } from 'sonner'
import {
  frozenAttrs,
  unfrozenAttrs,
} from '#src/components/rich-text-editor/ObservationNode/observationFreeze.ts'
import { isForeignRecord } from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useObservationParticipant } from '#src/components/rich-text-editor/ObservationNode/useObservationParticipant.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

const NONE: SelectedObservation[] = []

interface SelectedObservation {
  pos: number
  attrs: ObservationNodeAttributes
}

export interface ObservationBulkFreeze {
  // How many selected nodes each action would change; 0 hides it.
  freezable: number
  frozen: number
  freezeLabel: string
  unfreezeLabel: string
  freeze: () => void
  unfreeze: () => void
}

// Freezing and unfreezing every observation node in the editor's selection
// at once, as one change (one undo step). Nodes holding another
// participant's record are left out. Behind the structured capture flag.
export function useObservationBulkFreeze(
  editor: Editor | null
): ObservationBulkFreeze | null {
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const isReadOnly = useIsReadOnly()
  const recordFor = useObservationParticipant()
  const medplum = useMedplum()
  const openConfirmation = useDeleteConfirmationStore((state) => state.open)

  const selected =
    useEditorState({
      editor,
      selector: ({ editor: current }) =>
        current ? selectedObservations(current.state) : NONE,
      equalityFn: isEqual,
    }) ?? NONE

  // The records the selection names, to leave another participant's out.
  const recordIds = useMemo(
    () =>
      selected
        .flatMap(({ attrs }) => [
          ...observationReferenceStrings(attrs),
          ...(typeof attrs.frozenReference === 'string'
            ? [attrs.frozenReference]
            : []),
        ])
        .map((reference) => reference.split('/')[1])
        .filter((id): id is string => !!id),
    [selected]
  )
  const { data: records } = useSearch(
    ExtendedObservation,
    inlineCapture && recordIds.length > 0
      ? ExtendedObservation.queryByIds(recordIds)
      : undefined,
    { silentErrors: true }
  )

  if (!inlineCapture || isReadOnly || !editor?.isEditable) {
    return null
  }

  const own = selected.filter(({ attrs }) => {
    const reference =
      (attrs.reference as string | null) ??
      (attrs.frozenReference as string | null)
    const record = records?.find(
      (candidate) => candidate.reference.reference === reference
    )
    return !isForeignRecord(record, recordFor)
  })
  const toFreeze = own.filter(
    ({ attrs }) => attrs.mode !== OBSERVATION_MODE_LATEST
  )
  const toUnfreeze = own.filter(
    ({ attrs }) => attrs.mode === OBSERVATION_MODE_LATEST
  )

  const freeze = (): void => {
    const nodes = toFreeze
    const apply = (): void => {
      const { tr } = editor.state
      for (const { pos, attrs } of nodes) {
        tr.setNodeMarkup(pos, undefined, { ...attrs, ...frozenAttrs(attrs) })
      }
      editor.view.dispatch(tr)
      toast.success(`Frozen ${count(nodes.length, 'observation')}`, {
        action: {
          label: 'Undo',
          // Each back as it was, in one change, where it is still frozen.
          onClick: () => {
            const undo = editor.state.tr
            for (const { pos, attrs } of nodes) {
              const node = undo.doc.nodeAt(pos)
              if (
                node?.type.name === 'observation' &&
                node.attrs.mode === OBSERVATION_MODE_LATEST
              ) {
                undo.setNodeMarkup(pos, undefined, attrs)
              }
            }
            editor.view.dispatch(undo)
          },
        },
      })
    }
    const recorded = nodes.filter(
      ({ attrs }) => observationReferenceStrings(attrs).length > 0
    ).length
    if (recorded === 0) {
      apply()
      return
    }
    openConfirmation({
      title: `Freeze ${count(nodes.length, 'observation')}?`,
      description: `${recorded} ${recorded === 1 ? 'has a recorded answer' : 'have recorded answers'}. The field${nodes.length === 1 ? '' : 's'} will show the latest recorded value instead; the recorded answers are kept.`,
      deleteText: 'Freeze',
      onConfirm: apply,
    })
  }

  const unfreeze = (): void => {
    const nodes = toUnfreeze
    // Each set-aside record looked up now, so one deleted while frozen is
    // not brought back; then all of them in one change.
    void Promise.all(
      nodes.map(async ({ attrs }) => {
        const reference = attrs.frozenReference
        if (typeof reference !== 'string') {
          return false
        }
        try {
          const record = await medplum.readReference<Observation>({
            reference,
          })
          return record.status !== 'entered-in-error'
        } catch {
          return false
        }
      })
    ).then((keeps) => {
      if (editor.isDestroyed) {
        return
      }
      const { tr } = editor.state
      nodes.forEach(({ pos, attrs }, index) => {
        const node = tr.doc.nodeAt(pos)
        if (
          node?.type.name === 'observation' &&
          node.attrs.mode === OBSERVATION_MODE_LATEST &&
          node.attrs.frozenReference === attrs.frozenReference
        ) {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            ...unfrozenAttrs(attrs, keeps[index] ?? false),
          })
        }
      })
      editor.view.dispatch(tr)
      toast.success(`Made ${count(nodes.length, 'observation')} answerable`)
    })
  }

  return {
    freezable: toFreeze.length,
    frozen: toUnfreeze.length,
    freezeLabel: `Freeze ${count(toFreeze.length, 'observation')}`,
    unfreezeLabel: `Unfreeze ${toUnfreeze.length}`,
    freeze,
    unfreeze,
  }
}

function selectedObservations(state: EditorState): SelectedObservation[] {
  const { from, to, empty } = state.selection
  if (empty) {
    return []
  }
  const found: SelectedObservation[] = []
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'observation') {
      found.push({ pos, attrs: node.attrs as ObservationNodeAttributes })
    }
    return true
  })
  return found
}

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}
